import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ContactField } from '@heybesafe/shared';
import { ensureUrlScheme } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import LoadingPane from '../components/LoadingPane';
import { HbsInput } from '../components/hbs/Input';
import { HbsButton } from '../components/hbs/Button';
import { Icon } from '../components/hbs/Icon';
import { ContactAvatar } from '../components/ContactAvatar';
import { ShareContactWithFamilyAction } from '../components/ShareContactWithFamilyAction';

function CopyAction({ value, label }: { value: string; label: string }): JSX.Element {
  return (
    <button
      // One per field, so the fixed internal field label namespaces the id.
      id={`contact-copy-${label.toLowerCase().replace(/\s+/g, '-')}-button`}
      type="button"
      aria-label={`Copy ${label}`}
      onClick={() => void navigator.clipboard.writeText(value)}
      className="p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
    >
      <Icon name="copy" size={16} />
    </button>
  );
}

/** A group of labelled values (phones / emails / addresses) as read-only rows.
 *  `href` builds a tel:/mailto: link so a value is one click to dial or mail. */
function FieldGroup({
  title,
  fields,
  href,
}: {
  title: string;
  fields: ContactField[];
  href?: (value: string) => string;
}): JSX.Element | null {
  const rows = fields.filter((f) => f.value.trim());
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <div className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
        {title}
      </div>
      {rows.map((f, i) => (
        <HbsInput
          key={`${f.label}-${i}`}
          // A labelless row shows no label rather than falling back to the
          // group title — otherwise an unlabelled email renders "Email" both
          // as the group header and again where the row label would go.
          label={f.label || undefined}
          value={f.value}
          onChange={() => {}}
          readOnly
          trailingActions={
            <>
              {href && (
                <a
                  href={href(f.value)}
                  aria-label={`Open ${f.label || title}`}
                  className="p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt inline-flex"
                >
                  <Icon name="launch" size={16} />
                </a>
              )}
              <CopyAction value={f.value} label={f.label || title} />
            </>
          }
        />
      ))}
    </div>
  );
}

export default function ContactDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { items, itemsLoaded, deleteItem } = useSafe();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const item = items.find((i) => i.id === id && i.type === 'contact' && !i.orgId);

  if (!item) {
    // items is emptied on lock and only repopulated by the post-unlock
    // syncPull, so an absent item means loading until itemsLoaded.
    if (!itemsLoaded) return <LoadingPane label="Loading contact…" />;
    return (
      <div className="h-full flex flex-col items-center justify-center text-hbs-text-mute gap-3">
        <p className="text-[14px]">Contact not found.</p>
        <HbsButton variant="plain" id="contact-back-button" onClick={() => navigate('/contacts')}>
          Back to contacts
        </HbsButton>
      </div>
    );
  }

  const websiteHref = item.website ? ensureUrlScheme(item.website) : '';
  const subtitle = [item.role, item.company].filter(Boolean).join(' · ');
  const hasDetails =
    !!(item.phones?.length || item.emails?.length || item.addresses?.length || item.website || item.notes);

  async function handleDelete(): Promise<void> {
    setDeleting(true);
    try {
      await deleteItem(item!.id);
      navigate('/contacts');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="p-7 flex flex-col gap-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start gap-4">
        <ContactAvatar name={item.name} size={64} />
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-semibold text-[30px] tracking-hbs-tighter text-hbs-text truncate">
            {item.name}
          </h2>
          <p className="text-[13px] text-hbs-text-mute mt-1">
            {subtitle || 'Contact'}
          </p>
        </div>
      </div>

      {/* Details card */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        {hasDetails ? (
          <div className="flex flex-col gap-6">
            {item.phones && (
              <FieldGroup title="Phone" fields={item.phones} href={(v) => `tel:${v.replace(/\s+/g, '')}`} />
            )}
            {item.emails && (
              <FieldGroup title="Email" fields={item.emails} href={(v) => `mailto:${v}`} />
            )}
            {item.addresses && <FieldGroup title="Address" fields={item.addresses} />}
            {item.website && (
              <HbsInput
                label="Website"
                value={item.website}
                onChange={() => {}}
                readOnly
                trailingActions={
                  <>
                    <a
                      href={websiteHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open website"
                      className="p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt inline-flex"
                    >
                      <Icon name="launch" size={16} />
                    </a>
                    <CopyAction value={item.website} label="website" />
                  </>
                }
              />
            )}
            {item.notes && (
              <HbsInput
                label="Notes"
                value={item.notes}
                onChange={() => {}}
                readOnly
                trailingActions={<CopyAction value={item.notes} label="notes" />}
              />
            )}
          </div>
        ) : (
          <p className="text-[13px] text-hbs-text-mute">
            No details yet. Choose Edit to add phone numbers, emails, and more.
          </p>
        )}

        <div className="mt-6 flex justify-between items-center">
          <div className="flex items-center gap-1">
            <HbsButton variant="plain" id="contact-edit-button" onClick={() => navigate(`/contacts/${item.id}/edit`)}>
              Edit
            </HbsButton>
            {/* key forces a remount per contact so the share confirmation
                doesn't leak onto the next contact viewed on the same route. */}
            <ShareContactWithFamilyAction key={item.id} item={item} />
          </div>
          {!confirming ? (
            <HbsButton variant="plain" id="contact-delete-button" onClick={() => setConfirming(true)}>
              Delete
            </HbsButton>
          ) : (
            <div className="flex gap-2">
              <HbsButton variant="ghost" id="contact-delete-cancel-button" onClick={() => setConfirming(false)}>
                Cancel
              </HbsButton>
              <HbsButton variant="accent" loading={deleting} id="contact-delete-confirm-button" onClick={handleDelete}>
                Confirm
              </HbsButton>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
